import { z } from "zod";
import type { SimulationFormStep } from "./ui-state.js";

const numberLikeSchema = z.union([z.string(), z.number()]).optional();
const consentSchema = z
  .boolean()
  .refine((value) => value, "Le consentement est obligatoire.");

export const simulationIntroStepSchema = z.object({});

export const simulationBienStepSchema = z
  .object({
    prixAchat: numberLikeSchema,
    loyerAnnuel: numberLikeSchema,
  })
  .superRefine((input, ctx) => {
    validateRequiredPositive("prixAchat", input.prixAchat, ctx);
    validateRequiredPositive("loyerAnnuel", input.loyerAnnuel, ctx);
  });

export const simulationFinancementStepSchema = z
  .object({
    apport: numberLikeSchema,
    dureePret: numberLikeSchema,
    taux: numberLikeSchema,
  })
  .superRefine((input, ctx) => {
    validateRequiredNonNegative("apport", input.apport, ctx);
    validateRequiredPositive("dureePret", input.dureePret, ctx);
    validateRequiredPositive("taux", input.taux, ctx);
  });

export const simulationContactStepSchema = z.object({
  email: z.string().email("Email invalide."),
  firstName: z.string().trim().max(120, "Le prenom est trop long."),
  source: z
    .string()
    .trim()
    .min(1, "La source est obligatoire.")
    .max(120, "La source est trop longue."),
  consent: consentSchema,
  honeypot: z.string().max(0, "Requete invalide.").optional(),
});

export const simulationFormSchema = z
  .object({
    email: z.string().email("Email invalide."),
    firstName: z.string().trim().max(120, "Le prenom est trop long."),
    source: z
      .string()
      .trim()
      .min(1, "La source est obligatoire.")
      .max(120, "La source est trop longue."),
    consent: consentSchema,
    honeypot: z.string().max(0).optional(),
    prixAchat: numberLikeSchema,
    loyerAnnuel: numberLikeSchema,
    apport: numberLikeSchema,
    dureePret: numberLikeSchema,
    taux: numberLikeSchema,
  })
  .superRefine((input, ctx) => {
    validateRequiredPositive("prixAchat", input.prixAchat, ctx);
    validateRequiredPositive("loyerAnnuel", input.loyerAnnuel, ctx);
    validateRequiredNonNegative("apport", input.apport, ctx);
    validateRequiredPositive("dureePret", input.dureePret, ctx);
    validateRequiredPositive("taux", input.taux, ctx);
  });

export const simulationStatutStepSchema = z.object({});

export const simulationStepSchemas: Record<SimulationFormStep, z.ZodTypeAny> = {
  intro: simulationIntroStepSchema,
  statut: simulationStatutStepSchema,
  bien: simulationBienStepSchema,
  financement: simulationFinancementStepSchema,
  contact: simulationContactStepSchema,
  result: z.object({}),
  "expert-optin": z.object({}),
};

function parseNumberLike(value: string | number | undefined): number | undefined {
  if (value === undefined || value === null || value === "") return undefined;
  const parsed =
    typeof value === "number" ? value : Number(value.replaceAll(/\s+/g, ""));
  return Number.isFinite(parsed) ? parsed : Number.NaN;
}

function validateRequiredPositive(
  fieldName: string,
  value: string | number | undefined,
  ctx: z.RefinementCtx,
): void {
  if (value === undefined || value === null || (typeof value === "string" && !value.trim())) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `\`${fieldName}\` est obligatoire.`,
    });
    return;
  }
  const parsed = parseNumberLike(value);
  if (parsed === undefined || !Number.isFinite(parsed) || parsed <= 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `\`${fieldName}\` doit etre un nombre positif.`,
    });
  }
}

function validateRequiredNonNegative(
  fieldName: string,
  value: string | number | undefined,
  ctx: z.RefinementCtx,
): void {
  if (value === undefined || value === null || (typeof value === "string" && !value.trim())) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `\`${fieldName}\` est obligatoire.`,
    });
    return;
  }
  const parsed = parseNumberLike(value);
  if (parsed === undefined || !Number.isFinite(parsed) || parsed < 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `\`${fieldName}\` doit etre un nombre positif ou nul.`,
    });
  }
}
